/* eslint-disable camelcase */
import React, { useEffect } from 'react';
import axios from 'axios';
import { useMutation } from '@apollo/client';
import {
  CREATE_FORM_ANSWER,
  CREATE_WIZARD_SUBMISSION,
  UPDATE_WIZARD_SUBMISSION_RESULT,
} from '../../../queries/form';
import {
  Enum_Feedbacksubmission_Type,
  FeedbackSubmissionInput,
  Form,
  WizardSubmissionInput,
  ComponentComponentsFormSectionWithAnswersInput,
} from '../../../types/schema';
import { AnswerState, SubmitStatus, SubmitStep } from '../types';
import logging from '../../../utils/logging';
import { generateSubmissionHash } from '../../../utils/string';
import {
  buildFormAnswersSummary,
  setHubspotFormSnapshot,
} from '../../../utils/hubspotFormSnapshot';

type UseFormSubmitProps = {
  form: Form & { id?: number };
  answers: AnswerState;
  setAnswers: React.Dispatch<React.SetStateAction<AnswerState>>;
  visibleQuestionTitles: Set<string>;
  isWizardMode: boolean;
  isEn: boolean;
  additionalInfo?: Record<string, string>;
  onSubmitSuccess?: (submissionHash: string) => void;
};

export const useFormSubmit = ({
  form,
  answers,
  setAnswers,
  visibleQuestionTitles,
  isWizardMode,
  isEn,
  additionalInfo,
  onSubmitSuccess,
}: UseFormSubmitProps) => {
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [submitCompleted, setSubmitCompleted] = React.useState<SubmitStatus>({
    sent: false,
    error: false,
  });
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitStep, setSubmitStep] = React.useState<SubmitStep>(null);
  const [submissionId, setSubmissionId] = React.useState<string | null>(null);
  const [submissionHash, setSubmissionHash] = React.useState<string | null>(null);

  const messageRef = React.useRef<HTMLDivElement>(null);
  const [createNewAnswer] = useMutation(CREATE_FORM_ANSWER);
  const [createWizardSubmission] = useMutation(CREATE_WIZARD_SUBMISSION);
  const [updateWizardSubmission] = useMutation(UPDATE_WIZARD_SUBMISSION_RESULT);

  const allQuestionsAnswered = React.useMemo(() => {
    for (const [question, answer] of Object.entries(answers)) {
      if (!visibleQuestionTitles.has(question)) continue;
      if (!answer.answer && answer.required) return false;
    }
    return true;
  }, [answers, visibleQuestionTitles]);

  const fetchPromptResults = async (): Promise<Record<string, string>> => {
    const results: Record<string, string> = {};
    const sections = form.formSections || [];

    // Passo 1: iterar respostas uma única vez e agrupar
    type QA = {
      question: string;
      answer: string | null;
      required?: boolean;
      hash?: string;
      answerOptions?: { title: string }[];
    };
    const globalQA: QA[] = [];
    const qaBySectionId: Record<string, QA[]> = {};

    for (const [question, answerData] of Object.entries(answers)) {
      const qa: QA = {
        question,
        answer: answerData.answer,
        required: answerData.required,
        hash: answerData.hash,
        answerOptions: answerData.answerOptions,
      };
      if (answerData.sectionId) {
        (qaBySectionId[answerData.sectionId] ||= []).push(qa);
      } else {
        globalQA.push(qa);
      }
    }

    // Passo 2: iterar seções e montar tarefas paralelas
    // Todas as respostas de perguntas geradas por IA (de qualquer seção) devem
    // ser incluídas no relatório, independente de qual seção tem promptResult: true
    const allPromptQA = Object.values(qaBySectionId).flat();

    const tasks: Promise<void>[] = [];
    for (const section of sections) {
      if (!section || !section.promptResult) continue;

      const questionsAndAnswers = [...globalQA, ...allPromptQA];

      const task = axios
        .post(
          '/api/processPromptResult',
          {
            questionsAndAnswers,
            formSlug: form.slug,
            prompt: section.promptResult,
            form: {
              title: form.title,
              descriptionSection: form.descriptionSection,
            },
            promptCommon: section.promptCommon,
          },
          { timeout: 150000 },
        )
        .then(({ data }) => {
          if (data.success && data.result) {
            results[section.id] = data.result;
          }
        });

      tasks.push(task);
    }

    await Promise.all(tasks);
    return results;
  };

  const buildWizardSections = (
    promptResults: Record<string, string>,
  ): ComponentComponentsFormSectionWithAnswersInput[] => {
    const sections = (form.formSections || []).filter(
      (section): section is NonNullable<typeof section> => !!section,
    );

    const wizardSections: ComponentComponentsFormSectionWithAnswersInput[] = [];
    for (const section of sections) {
      const sectionQuestionTitles = new Set(
        (section.questions || []).map((q) => q?.title).filter(Boolean),
      );

      const sectionAnswers = Object.entries(answers)
        .filter(
          ([question, a]) =>
            (a.sectionId === section.id || sectionQuestionTitles.has(question)) &&
            visibleQuestionTitles.has(question),
        )
        .map(([question, answerData]) => ({
          question,
          answer: answerData.answer,
          questionHash: answerData.hash,
        }));

      wizardSections.push({
        title: section.title,
        description: section.description,
        promptCommon: section.promptCommon,
        ...(sectionAnswers.length > 0 && { answers: sectionAnswers }),
        conditionalShowQuestion: section.conditionalShowQuestion,
        conditionalShowAnswer: section.conditionalShowAnswer,
        promptQuestion: section.promptQuestion,
        promptResult: section.promptResult,
        result: promptResults[section.id] || undefined,
        loaderText: section.loaderText,
      });
    }

    return wizardSections;
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);
    setSubmitCompleted({ sent: false, error: false });
    setSubmitLoading(true);

    try {
      let hasError = false;
      let updatedAnswers = { ...answers };

      Object.entries(answers).forEach(([question, answer]) => {
        if (!visibleQuestionTitles.has(question)) return;
        if (!answer.answer && answer.required) {
          updatedAnswers = {
            ...updatedAnswers,
            [question]: {
              ...updatedAnswers[question],
              error: isEn ? 'This field is required' : 'Esse campo é obrigatório',
            },
          };
          hasError = true;
        }
      });
      setAnswers(updatedAnswers);

      if (hasError) {
        setSubmitLoading(false);
        return;
      }

      let submissionHash: string | null = null;

      if (isWizardMode) {
        setSubmitStep('processing');
        const promptResults = await fetchPromptResults();

        const uniqueSlug = `${form.slug}-${Date.now()}`;
        const generatedHash = generateSubmissionHash();

        const wizardData: WizardSubmissionInput = {
          title: form.title,
          slug: uniqueSlug,
          hash: generatedHash,
          successMessage: form.successMessage,
          publishedAt: new Date().toISOString(),
          descriptionSection: form.descriptionSection
            ? {
                title: form.descriptionSection.title,
                description: form.descriptionSection.description,
              }
            : undefined,
          formSection: buildWizardSections(promptResults),
          ...(additionalInfo && Object.keys(additionalInfo).length > 0 && { additionalInfo }),
        };

        setSubmitStep('saving');
        const createResult = await createWizardSubmission({
          variables: { data: wizardData },
        });

        const createdId = createResult.data?.createWizardSubmission?.data?.id ?? null;
        setSubmissionId(createdId);
        setSubmissionHash(generatedHash);
        submissionHash = generatedHash;

        const answersSummary = buildFormAnswersSummary(
          Object.entries(answers)
            .filter(([question, answer]) => visibleQuestionTitles.has(question) && !!answer.answer)
            .map(([question, answer]) => ({
              question,
              answer: answer.answer || '',
            })),
        );

        const resultUrl =
          typeof window !== 'undefined'
            ? `${window.location.origin}/form/${form.slug}/${generatedHash}`
            : `/form/${form.slug}/${generatedHash}`;

        setHubspotFormSnapshot({
          form_id: form.id ? String(form.id) : undefined,
          form_title: form.title || undefined,
          form_slug: form.slug || undefined,
          wizard_submission_hash: generatedHash,
          wizard_submission_url: resultUrl,
          form_answers_summary: answersSummary || undefined,
        });
      } else {
        const submitAnswers = Object.entries(answers)
          .filter(([question]) => visibleQuestionTitles.has(question))
          .map(([question, answer]) => ({
            question,
            questionHash: answer.hash,
            answer: answer.answer,
            answerOptions: answer.answerOptions,
          }));

        const newForm: FeedbackSubmissionInput = {
          formTitle: form.title,
          formSlug: form.slug,
          answers: submitAnswers,
          type: 'default' as Enum_Feedbacksubmission_Type,
          ...(additionalInfo && Object.keys(additionalInfo).length > 0 && { additionalInfo }),
        };

        await createNewAnswer({
          variables: { submission: newForm },
        });
      }

      updatedAnswers = { ...answers };
      Object.entries(answers).forEach(([question]) => {
        updatedAnswers = {
          ...updatedAnswers,
          [question]: {
            ...updatedAnswers[question],
            error: null,
          },
        };
      });
      setAnswers(updatedAnswers);
      setSubmitCompleted({ sent: true, error: false });
      setSubmitStep(null);
      setSubmitLoading(false);

      if (submissionHash && onSubmitSuccess) {
        onSubmitSuccess(submissionHash);
      }
    } catch (err) {
      setSubmitStep(null);
      setSubmitLoading(false);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setSubmitError(errorMessage);
      setSubmitCompleted({ sent: false, error: true });
      logging.error('Error submitting form:', err as Error);
    }
  };

  const regenerateResult = async (): Promise<void> => {
    if (!submissionId || !submissionHash) return;
    setSubmitLoading(true);
    try {
      const promptResults = await fetchPromptResults();
      const updatedSections = buildWizardSections(promptResults);

      await updateWizardSubmission({
        variables: { id: submissionId, data: { formSection: updatedSections } },
      });

      setSubmitLoading(false);

      if (onSubmitSuccess) {
        onSubmitSuccess(submissionHash);
      }
    } catch (err) {
      setSubmitLoading(false);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setSubmitError(errorMessage);
      logging.error('Error regenerating result:', err as Error);
    }
  };

  useEffect(() => {
    if (submitCompleted.sent && !submitCompleted.error && messageRef.current)
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [submitCompleted.sent, submitCompleted.error, messageRef]);

  return {
    submitLoading,
    submitStep,
    submitCompleted,
    allQuestionsAnswered,
    handleSubmit,
    messageRef,
    submitError,
    submissionHash,
    regenerateResult,
  };
};
